import React from "react";
import { ReactNode } from "react";

import {
  Autocomplete,
  Box,
  TextField,
  InputAdornment,
  Paper,
  PaperProps,
} from "@mui/material";

interface EventEHRLazyDropdownProps {
  dropdownOptions: any;
  selectedValue: any;
  dropdownSearch: any;
  fullWidth?: string;
  placeholder?: string;
  label?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  helperText?: string;
  error?: boolean;
  isDisabled?: boolean;
  noOptionText?: string;
  clearSearch?: boolean;
  onBlur?: (e: any) => void;
  setResetFlag: (isTrue: boolean) => void;
  setPageCount: (data: any) => void;
  setSelectedValue: (data: any) => void;
  setDropdownSearch: (data: string) => void;
  dropdownIcon?: boolean;
  showCreateOption?: boolean;
  createOptionLabel?: string;
  onCreateClick?: (searchValue: string) => void;
  // Optional MUI Autocomplete filterOptions passthrough. Default (undefined) =
  // MUI's built-in client-side substring filter (unchanged for all existing
  // consumers). Consumers can pass a token-aware matcher, or `(x) => x` to
  // disable client filtering when results come pre-filtered from the server.
  filterOptions?: (options: any[], state: any) => any[];
}

const EventEHRLazyDropdown = ({
  dropdownOptions,
  selectedValue,
  dropdownSearch,
  fullWidth = "100%",
  placeholder,
  helperText = "",
  error = false,
  noOptionText,
  isDisabled = false,
  clearSearch = false,
  onBlur = (e) => {},

  setResetFlag,
  setPageCount,
  setSelectedValue,
  setDropdownSearch,
  label,
  startIcon,
  endIcon,
  dropdownIcon = false,
  showCreateOption = false,
  createOptionLabel = "+ Create",
  onCreateClick,
  filterOptions,
}: EventEHRLazyDropdownProps) => {
  // Keep the values the footer needs in refs so `CreateFooterPaper` can be a
  // stable component (see useCallback below). Passing a fresh PaperComponent
  // on every render makes MUI remount the whole popup, which resets the
  // listbox scroll to the top whenever the parent re-renders (e.g. the
  // scroll-to-bottom `setPageCount`). Reading from refs keeps the label and
  // onClick current without changing the component's identity.
  const dropdownSearchRef = React.useRef(dropdownSearch);
  const createOptionLabelRef = React.useRef(createOptionLabel);
  const onCreateClickRef = React.useRef(onCreateClick);
  dropdownSearchRef.current = dropdownSearch;
  createOptionLabelRef.current = createOptionLabel;
  onCreateClickRef.current = onCreateClick;

  const CreateFooterPaper = React.useCallback((props: PaperProps) => {
    const currentLabel = createOptionLabelRef.current;
    const currentSearch = dropdownSearchRef.current;
    const label = currentLabel.includes("{search}")
      ? currentLabel.replace("{search}", currentSearch || "")
      : currentLabel;
    return (
      <Paper {...props}>
        {props.children}
        <Box
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (onCreateClickRef.current) {
              onCreateClickRef.current(dropdownSearchRef.current || "");
            }
          }}
          sx={{
            position: "sticky",
            bottom: 0,
            padding: "10px 16px",
            borderTop: "1px solid #E0E0E0",
            backgroundColor: "#F8F9FB",
            color: "#005596",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
            "&:hover": { backgroundColor: "#EFF4FF" },
          }}
        >
          {label}
        </Box>
      </Paper>
    );
  }, []);

  const handleScrollDownEvent = () => {
    const lazyContainer = document.querySelector("#lazyDropdownSearch-listbox");
    if (
      lazyContainer &&
      dropdownOptions &&
      dropdownOptions.length > 0
      // &&
      // dropdownSearch.length === 0
    ) {
      lazyContainer.addEventListener("scroll", (event) => {
        if (
          Math.abs(
            lazyContainer.scrollHeight -
              lazyContainer.scrollTop -
              lazyContainer.clientHeight,
          ) < 5
        ) {
          setPageCount((count: number) => count + 1);
        }
      });
    }
  };

  return (
    <Autocomplete
      disabled={isDisabled}
      id="lazyDropdownSearch"
      size="small"
      {...(filterOptions ? { filterOptions } : {})}
      noOptionsText={noOptionText}
      value={selectedValue}
      options={dropdownOptions}
      getOptionLabel={(option: any) => option.label || ""}
      PaperComponent={showCreateOption ? CreateFooterPaper : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          label={label}
          // value={selectedValue}
          helperText={helperText}
          error={error}
          onBlur={onBlur}
          slotProps={{
            input: {
              ...params.InputProps,
              ...(startIcon
                ? {
                    startAdornment: (
                      <InputAdornment
                        style={{
                          paddingLeft: "10px",
                          marginRight: "0",
                          cursor: "pointer",
                        }}
                        position="start"
                      >
                        {startIcon}
                      </InputAdornment>
                    ),
                  }
                : {}),
              ...(dropdownIcon
                ? endIcon && {
                    endAdornment: (
                      <InputAdornment position="start">
                        {endIcon}
                      </InputAdornment>
                    ),
                  }
                : {
                    endAdornment: null, // Remove the default dropdown arrow
                  }),
            },
          }}
        />
      )}
      sx={{
        width: fullWidth,
        "& .MuiOutlinedInput-root": {
          paddingRight: "6px !important", // Adjust padding to match margin
        },
      }}
      renderOption={(props, option: any) => {
        const { key, ...otherProps } = props; // Destructure and exclude `key`
        return (
          <Box
            key={key} // Pass the `key` directly
            component="li"
            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
            {...otherProps} // Spread the remaining props
          >
            {/* <img loading="lazy" width="20" src={option.image} alt="" /> */}
            {option.label}
          </Box>
        );
      }}
      onInputChange={(event, value, reason) => {
        if (reason === "input") {
          setDropdownSearch(value);
        }
        if (
          (reason === "input" && value.length === 0) ||
          (reason === "reset" && event?.type === "blur")
        ) {
          setTimeout(() => {
            handleScrollDownEvent();
          }, 100);
          setResetFlag(true);
        }
      }}
      ListboxProps={{
        style: {
          maxHeight: "200px", // Set the maximum height
          overflowY: "auto", // Enable scrolling
        },
      }}
      onChange={(event, option, reason) => {
        if (reason === "selectOption") {
          setSelectedValue(option);
        }
        if (reason === "clear") {
          if (dropdownSearch.length > 0) {
            setResetFlag(true);
          } else {
            setSelectedValue(null);
          }
        }
      }}
      onClose={() => {
        clearSearch ? setResetFlag(true) : setDropdownSearch("");
      }}
      onOpen={() => {
        // 'onOpen' is fired when the popup requests to be opened. We need setTimeout for the component to load.
        setTimeout(() => {
          handleScrollDownEvent();
        }, 100);
      }}
    />
  );
};

export default EventEHRLazyDropdown;
